import { createAsyncThunk } from '@reduxjs/toolkit';
import debug from '../utils/debug';
import { OrderingConstants, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import history from '../history';
import { setCurrentModal } from './setCurrentModal';
import { MESSAGE_MODAL_ID } from '../modals/MessageModal';
import i18next from 'i18next';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { RootState } from 'store';
import getConfig from '../selectors/getConfig';
import { MENU_SCREEN_ROUTE, PICK_BRAND_SCREEN_ROUTE } from '../hooks/useRoutes';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import { ITEM_CART_AVAILABILITY_MODAL } from '../modals/ItemCartAvailabilityModal';
import { updateLoadingDependencies } from '../slices/loadingDependencies';

const {
  updateKeyOrderProperty,
  setPickupTime,
  setBufferPickupTime,
  setBypassDeliveryDetails,
  removeStagedPurchase,
} = OrderingOperations;

const {
  getLocationBrandsWithMenuRepresentation,
  getBypassDeliveryDetails,
  getBufferWarnings,
  getStagedPurchases,
  getSaleType,
  getDeliveryTime,
} = OrderingSelectors;

const { SALE_TYPE } = OrderingConstants;

export const adjustOrder = createAsyncThunk(
  'adjustOrder',
  async (
    data: {
      saleType?: SALE_TYPE;
      locationId?: string;
      bypassDeliveryDetailsInit?: boolean;
      confirmLocationDeliveryEstimate?: boolean;
      updateLoadingDependency?: boolean;
      isASAP?: boolean;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const {
      saleType,
      locationId,
      bypassDeliveryDetailsInit,
      confirmLocationDeliveryEstimate,
      updateLoadingDependency = true,
      isASAP,
    } = data;
    debug('adjusting order', {
      newSaleType: saleType,
      newLocationId: locationId,
      confirmLocationDeliveryEstimate,
    });
    try {
      const bypassDeliveryDetails = getBypassDeliveryDetails(getState() as EntireFrontendState);
      if (updateLoadingDependency) {
        dispatch(updateLoadingDependencies({ handleAdjustOrderInProgress: true }));
      }

      const saleTypesThatShouldntResetPickupTime = [SALE_TYPE.CATERING];
      const dontResetPickupTime =
        saleType && saleTypesThatShouldntResetPickupTime.includes(saleType);

      if (!dontResetPickupTime) {
        dispatch(setPickupTime(null));
      }

      // If the user is explicitly switching from catering delivery to catering pickup, we want
      // to bring the time they have previously set for delivery across to their pickup time.
      const currentSaleType = getSaleType(getState() as EntireFrontendState);
      if (currentSaleType === SALE_TYPE.CATERING_DELIVERY && saleType === SALE_TYPE.CATERING) {
        const deliveryTime = getDeliveryTime(getState() as EntireFrontendState);
        if (deliveryTime) {
          dispatch(setPickupTime(deliveryTime));
          dispatch(setBufferPickupTime(deliveryTime));
        }
      }

      await dispatch(
        updateKeyOrderProperty({
          saleType,
          locationId,
          confirmLocationDeliveryEstimate,
          autoApply: true,
          bypassDeliveryDetails: bypassDeliveryDetailsInit || bypassDeliveryDetails,
          isASAP,
        }),
      ).unwrap();

      let popUpTitle = i18next.t(`preSaleMessage.title.${saleType}`);
      let popUpMessage = i18next.t(`preSaleMessage.message.${saleType}`);
      let popUpAction;

      const representedBrands = getLocationBrandsWithMenuRepresentation(
        getState() as EntireFrontendState,
      );

      const multipleBrands = representedBrands.length > 1;

      const deviceTypeMobile = getDeviceTypeMobile(getState() as RootState);
      const enableBrandSelection = getConfig(getState() as RootState).enableBrandSelection;
      const stagedPurchases = getStagedPurchases(getState() as EntireFrontendState);
      const showBrandPicker = deviceTypeMobile && enableBrandSelection && multipleBrands;

      // This sometimes throws an error, if a race condition fails between adjustOrder thunk and allLocations data.
      const warnings = getBufferWarnings(getState() as EntireFrontendState);

      const invalidStagedPurchases = stagedPurchases.filter(purchase => !purchase.valid);
      let stagedPurchasesToBeRemovedIds: string[] = [];

      // Storing invalid purchases ids to remove
      if (invalidStagedPurchases.length) {
        const invalidStagedPurchasesIds = invalidStagedPurchases.map(purchase => purchase.id);
        stagedPurchasesToBeRemovedIds = [
          ...stagedPurchasesToBeRemovedIds,
          ...invalidStagedPurchasesIds,
        ];
      }

      // Showing alert and setting staged purchases empty if we have any warnings
      if (warnings && warnings.length) {
        // Storing all unavailable items in one array
        let allUnavailableItems = warnings.reduce((acc: any, obj) => {
          if (obj.unavailableItems) {
            acc.push(...obj.unavailableItems);
          }
          return acc;
        }, []);

        // Handling if there any duplicates
        allUnavailableItems = Array.from(new Set(allUnavailableItems));

        // Getting things that needs to be removed from user cart
        const stagedPurchasesToBeRemoved = stagedPurchases.filter(purchase =>
          allUnavailableItems.includes(purchase.item.id),
        );

        // Storing ids of those items that needs to be removed
        stagedPurchasesToBeRemovedIds = [
          ...stagedPurchasesToBeRemovedIds,
          ...stagedPurchasesToBeRemoved.map(purchase => purchase.id),
        ];
      }

      if (stagedPurchasesToBeRemovedIds.length) {
        // Removing each purchase that needs to be removed from cart
        stagedPurchasesToBeRemovedIds.forEach(async id => {
          await dispatch(removeStagedPurchase(id));
        });

        dispatch(setCurrentModal(null));

        dispatch(
          setCurrentModal({
            modalId: ITEM_CART_AVAILABILITY_MODAL,
            clearReturnModalDetails: true,
          }),
        );
      } else {
        dispatch(setCurrentModal(null));
      }

      if (bypassDeliveryDetailsInit) {
        dispatch(setBypassDeliveryDetails(true));
      }

      // retrieve any purchases which are unavailable since adjustments
      // const unavailablePurchases = getUnavailablePurchases(getState() as EntireFrontendState);
      // if (unavailablePurchases.length) {
      //   popUpTitle = i18next.t('unavailablePurchasesWarning.title');
      //   popUpMessage = i18next.t('unavailablePurchasesWarning.text');
      //   popUpAction = () => dispatch(setCurrentModal({ modalId: ORDER_SUMMARY_MODAL_ID }));
      // }

      const modalParams = (getState() as RootState).modalParams;

      if (!bypassDeliveryDetails && modalParams?.allowRedirect !== false) {
        history.replace(showBrandPicker ? PICK_BRAND_SCREEN_ROUTE : MENU_SCREEN_ROUTE);
      }

      if (popUpTitle !== '' || popUpMessage !== '') {
        dispatch(
          setCurrentModal({
            modalId: MESSAGE_MODAL_ID,
            params: {
              title: popUpTitle,
              message: popUpMessage,
              action: popUpAction,
            },
            clearReturnModalDetails: true,
          }),
        );
      }
      if (updateLoadingDependency)
        dispatch(updateLoadingDependencies({ handleAdjustOrderInProgress: false }));
    } catch (e) {
      enqueueErrorSnackbar('Adjust order thunk failed');
      if (updateLoadingDependency)
        dispatch(updateLoadingDependencies({ handleAdjustOrderInProgress: false }));
      return rejectWithValue(e);
    }
  },
);
